import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../redux/store/hooks';
import { switchAccount } from '../../redux/features/authSlice';
import { useDebounce } from '../../hooks/useDebounce';
import {
  useEmployees,
  useUpdateEmployee,
  useDeleteEmployee,
  useBulkDeleteEmployees,
  useBulkUpdateEmployeePayroll,
  useDepartments,
  useDesignations,
  useSpecializations,
} from '../../services/httpServices/queries';
import { Badge } from '../../components/ui/badge';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  Loader2,
  AlertCircle,
  Search,
  X,
  Eye,
  Edit3,
  ToggleLeft,
  ToggleRight,
  SlidersHorizontal,
  Trash2,
  LogIn,
} from 'lucide-react';

interface ConfirmModalState {
  open: boolean;
  employeeId: string | null;
  employeeName: string;
  nextStatus: boolean;
}

interface DeleteRequestState {
  ids: string[];
  label: string;
}

function getInitials(firstName?: string, lastName?: string) {
  const f = firstName?.charAt(0) || '';
  const l = lastName?.charAt(0) || '';
  return (f + l).toUpperCase() || '?';
}

const WEEKDAY_SHORT: Record<number, string> = {
  0: 'Sun',
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
};

/** Employee-specific off days; `null` means the payroll policy fallback. */
function offDayLabels(days?: number[] | null): string[] {
  if (!Array.isArray(days) || days.length === 0) return [];
  return days
    .map((day) => WEEKDAY_SHORT[day])
    .filter(Boolean);
}

function getAvatarColor(name: string) {
  const colors = [
    'bg-teal-100 text-teal-700',
    'bg-blue-100 text-blue-700',
    'bg-indigo-100 text-indigo-700',
    'bg-rose-100 text-rose-700',
    'bg-amber-100 text-amber-700',
    'bg-emerald-100 text-emerald-700',
    'bg-cyan-100 text-cyan-700',
    'bg-violet-100 text-violet-700',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

export function Employees() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const isSuperAdmin = !!user?.roles?.includes('super_admin');
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const limit = 20;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });

  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);
  const [departmentId, setDepartmentId] = useState(() => params.get('departmentId') || '');
  const [designationId, setDesignationId] = useState(() => params.get('designationId') || '');
  const [specializationId, setSpecializationId] = useState(() => params.get('specializationId') || '');
  const [status, setStatus] = useState(() => params.get('status') || '');
  const [showFilters, setShowFilters] = useState(false);

  const filters = {
    search: debouncedSearch || undefined,
    departmentId: departmentId || undefined,
    designationId: designationId || undefined,
    specializationId: specializationId || undefined,
    status: status || undefined,
  };

  const { data, isLoading, error } = useEmployees(page, limit, filters);
  const updateEmployee = useUpdateEmployee();
  const deleteEmployee = useDeleteEmployee();
  const bulkDeleteEmployee = useBulkDeleteEmployees();
  const bulkUpdatePayroll = useBulkUpdateEmployeePayroll();

  const { data: departmentsData } = useDepartments();
  const { data: designationsData } = useDesignations();
  const { data: specializationsData } = useSpecializations();

  const departments = departmentsData?.data || [];
  const designations = designationsData?.data || [];
  const specializations = specializationsData?.data || [];

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    if (departmentId) newParams.set('departmentId', departmentId);
    if (designationId) newParams.set('designationId', designationId);
    if (specializationId) newParams.set('specializationId', specializationId);
    if (status) newParams.set('status', status);

    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, departmentId, designationId, specializationId, status, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search, departmentId, designationId, specializationId, status]);

  const [confirm, setConfirm] = useState<ConfirmModalState>({
    open: false,
    employeeId: null,
    employeeName: '',
    nextStatus: false,
  });

  const [deleteRequest, setDeleteRequest] = useState<DeleteRequestState | null>(null);

  const employees = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;
  const trashedCount = Number(data?.stats?.trashed ?? 0);

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(employees.map((e: any) => e.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [employees]);

  // The logged-in user's own row can never be selected for bulk deletion.
  /** True when the row belongs to the currently logged-in user. */
  const isSelf = useCallback(
    (employee: any) =>
      !!user?.id && (employee.userId === user.id || employee.user?.id === user.id),
    [user?.id],
  );

  const selectableEmployees = useMemo(
    () => employees.filter((e: any) => !isSelf(e)),
    [employees, isSelf],
  );

  const allSelected = selectableEmployees.length > 0 && selectableEmployees.every((e: any) => selectedIds.has(e.id));
  const someSelected = employees.some((e: any) => selectedIds.has(e.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected =
        selectableEmployees.length > 0 && selectableEmployees.every((e: any) => prev.has(e.id));
      return everySelected ? new Set() : new Set(selectableEmployees.map((e: any) => e.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const employeeName = (employee: any) =>
    `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.name || 'this employee';

  const requestDeleteOne = (employee: any) => {
    if (isSelf(employee)) return;
    setDeleteRequest({ ids: [employee.id], label: employeeName(employee) });
  };

  const requestDeleteSelected = () => {
    const ids = Array.from(selectedIds).filter((id) => {
      const emp = employees.find((e: any) => e.id === id);
      return !emp || !isSelf(emp);
    });
    if (ids.length === 0) return;
    setDeleteRequest({ ids, label: '' });
  };

  const handleConfirmDelete = async () => {
    if (!deleteRequest || deleteRequest.ids.length === 0) return;
    // Never allow the logged-in user's own record to be deleted, even if it
    // slipped into the selection (e.g. the auth profile loaded after selecting).
    const ids = deleteRequest.ids.filter((id) => {
      const emp = employees.find((e: any) => e.id === id);
      return !emp || !isSelf(emp);
    });
    if (ids.length === 0) {
      setDeleteRequest(null);
      toast.error('You cannot delete your own account while logged in.');
      return;
    }
    try {
      if (ids.length === 1) {
        await deleteEmployee.mutateAsync(ids[0]);
      } else {
        await bulkDeleteEmployee.mutateAsync(ids);
      }
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      toast.success(`Moved ${ids.length} employee${ids.length === 1 ? '' : 's'} to Trash`);
    } catch {
      toast.error('Failed to delete employee(s)');
    } finally {
      setDeleteRequest(null);
    }
  };

  const openToggleConfirm = (employee: any) => {
    setConfirm({
      open: true,
      employeeId: employee.id,
      employeeName: employeeName(employee),
      nextStatus: !employee.isActive,
    });
  };

  const closeConfirm = () => {
    setConfirm({ open: false, employeeId: null, employeeName: '', nextStatus: false });
  };

  const handleToggleStatus = async () => {
    if (!confirm.employeeId) return;
    try {
      await updateEmployee.mutateAsync({
        id: confirm.employeeId,
        data: { isActive: confirm.nextStatus },
      });
      closeConfirm();
    } catch {
      // handled globally
    }
  };

  const handleTogglePayroll = (employee: any) => {
    const next = employee.payrollEnabled === false;
    updateEmployee.mutate(
      { id: employee.id, data: { payrollEnabled: next } },
      {
        onSuccess: () =>
          toast.success(`Payroll ${next ? 'enabled' : 'disabled'} for ${employeeName(employee)}`),
      },
    );
  };

  const handleBulkPayroll = async (payrollEnabled: boolean) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const response = await bulkUpdatePayroll.mutateAsync({ ids, payrollEnabled });
      const updated = response.data?.updated ?? ids.length;
      setSelectedIds(new Set());
      toast.success(
        `Payroll ${payrollEnabled ? 'enabled' : 'disabled'} for ${updated} employee${updated === 1 ? '' : 's'}`,
      );
    } catch {
      // Error toast is handled by the global MutationCache.
    }
  };

  const handleSwitchAccount = async (employee: any) => {
    const linkUserId = employee.userId || employee.user?.id;
    if (!linkUserId) {
      toast.error('This employee has no linked user account to switch to.');
      return;
    }
    try {
      setSwitchingId(employee.id);
      await dispatch(switchAccount(linkUserId)).unwrap();
      toast.success(`Switched to ${employeeName(employee)}`);
      // Reload so all cached data is rebuilt for the new identity.
      window.location.assign('/');
    } catch (error: any) {
      toast.error(typeof error === 'string' ? error : 'Failed to switch account');
      setSwitchingId(null);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setDepartmentId('');
    setDesignationId('');
    setSpecializationId('');
    setStatus('');
    setPage(1);
  };

  const hasActiveFilters = search || departmentId || designationId || specializationId || status;
  const deletePending = deleteEmployee.isPending || bulkDeleteEmployee.isPending;

  return (
    <div className="space-y-5">
      {/* Confirmation Modal */}
      {confirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border p-6 max-w-sm w-full space-y-4">
            <h3 className="text-lg font-semibold text-text">Confirm Status Change</h3>
            <p className="text-sm text-text-muted">
              Are you sure you want to <strong>{confirm.nextStatus ? 'activate' : 'deactivate'}</strong>{' '}
              <strong>{confirm.employeeName}</strong>?
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={closeConfirm}
                className="px-4 py-2 text-text-muted hover:text-text transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleToggleStatus}
                disabled={updateEmployee.isPending}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2 ${
                  confirm.nextStatus
                    ? 'bg-success text-white hover:bg-success-dark'
                    : 'bg-danger text-white hover:bg-danger-dark'
                }`}
              >
                {updateEmployee.isPending && <Loader2 size={16} className="animate-spin" />}
                {confirm.nextStatus ? 'Activate' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-primary" />
          <h1 className="text-xl font-semibold text-text">Employees</h1>
          {meta && (
            <Badge variant="default" className="ml-1">
              {meta.total}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/employees/trash')}
            className="px-3 py-2 border border-border text-text-muted hover:text-danger hover:border-danger rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
            title="View trash"
          >
            <Trash2 size={16} />
            Trash
            {trashedCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[0.625rem] font-semibold leading-none">
                {trashedCount > 99 ? '99+' : trashedCount}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/employees/new')}
            className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            Add Employee
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-surface rounded-xl border border-border p-3 space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by name, email, phone, or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters((s) => !s)}
              className={`px-3 py-2 border rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                showFilters || hasActiveFilters
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-border text-text-muted hover:text-text hover:bg-background'
              }`}
            >
              <SlidersHorizontal size={16} />
              Filters
              {hasActiveFilters && (
                <span className="w-2 h-2 rounded-full bg-primary" />
              )}
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5"
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 pt-1 border-t border-border">
            <select
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">All Departments</option>
              {departments.map((d: any) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <select
              value={designationId}
              onChange={(e) => setDesignationId(e.target.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">All Designations</option>
              {designations.map((d: any) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <select
              value={specializationId}
              onChange={(e) => setSpecializationId(e.target.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">All Specializations</option>
              {specializations.map((s: any) => (
                <option key={s.id} value={s.id}>{s.label || s.name}</option>
              ))}
            </select>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        )}
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => handleBulkPayroll(true)}
              disabled={bulkUpdatePayroll.isPending || deletePending}
              className="px-3 py-1.5 bg-success/10 text-success border border-success/20 rounded-lg hover:bg-success/20 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkUpdatePayroll.isPending ? <Loader2 size={14} className="animate-spin" /> : <ToggleRight size={14} />}
              Enable Payroll
            </button>
            <button
              type="button"
              onClick={() => handleBulkPayroll(false)}
              disabled={bulkUpdatePayroll.isPending || deletePending}
              className="px-3 py-1.5 bg-background text-text-muted border border-border rounded-lg hover:text-text transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkUpdatePayroll.isPending ? <Loader2 size={14} className="animate-spin" /> : <ToggleLeft size={14} />}
              Disable Payroll
            </button>
            <button
              type="button"
              onClick={requestDeleteSelected}
              disabled={deletePending || bulkUpdatePayroll.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteEmployee.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={deletePending || bulkUpdatePayroll.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-danger text-sm">
          <AlertCircle size={16} />
          <span>Failed to load employees. Please try again.</span>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}

      {/* Table */}
      {!isLoading && (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-2.5 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={selectableEmployees.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Employee</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Position</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Off days</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Status</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Payroll</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-text-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {employees.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-text-muted text-sm">
                      {hasActiveFilters ? (
                        <span>No employees match your filters.{' '}
                          <button onClick={clearFilters} className="text-primary hover:underline font-medium">
                            Clear filters
                          </button>
                        </span>
                      ) : (
                        <span>No employees found.{' '}
                          <button
                            onClick={() => navigate('/employees/new')}
                            className="text-primary hover:underline font-medium"
                          >
                            Register an employee
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                ) : (
                  employees.map((employee: any) => {
                    const name = `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Unknown';
                    const initials = getInitials(employee.firstName, employee.lastName);
                    const avatarClass = getAvatarColor(name);
                    const self = isSelf(employee);
                    const linkUserId = employee.userId || employee.user?.id;
                    const payrollOn = employee.payrollEnabled !== false;
                    const payrollPending =
                      updateEmployee.isPending && updateEmployee.variables?.id === employee.id;
                    const offDays = offDayLabels(employee.weeklyOffDays);
                    return (
                      <tr key={employee.id} className="hover:bg-background/50 transition-colors">
                        <td className="px-4 py-2.5">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(employee.id)}
                            onChange={() => toggleOne(employee.id)}
                            disabled={self}
                            title={self ? 'You cannot select your own account' : undefined}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                          />
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${avatarClass}`}>
                              {initials}
                            </div>
                            <div className="min-w-0">
                              <button
                                onClick={() => navigate(`/employees/${employee.slug || employee.id}`)}
                                className="block text-sm font-medium text-text hover:text-primary transition-colors text-left truncate"
                                title={name}
                              >
                                {name}
                              </button>
                              <span className="text-xs text-text-muted font-mono">
                                {employee.employeeCode || '—'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="text-sm text-text">
                            {employee.designation?.name || '—'}
                          </div>
                          <div className="text-xs text-text-muted">
                            {employee.department?.name || '—'}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          {offDays.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {offDays.map((day) => (
                                <span
                                  key={day}
                                  className="inline-flex rounded-md border border-border bg-background px-1.5 py-0.5 text-[0.6875rem] font-medium text-text"
                                >
                                  {day}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-text-muted">Policy default</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge variant={employee.isActive ? 'success' : 'danger'}>
                            {employee.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={payrollOn}
                            aria-label={`${payrollOn ? 'Disable' : 'Enable'} payroll for ${name}`}
                            onClick={() => handleTogglePayroll(employee)}
                            disabled={payrollPending}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-wait ${
                              payrollOn ? 'bg-success' : 'bg-border'
                            }`}
                            title={
                              payrollOn
                                ? 'Included in monthly payroll — click to disable'
                                : 'Excluded from monthly payroll — click to enable'
                            }
                          >
                            <span
                              className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${
                                payrollOn ? 'translate-x-[18px]' : 'translate-x-[3px]'
                              }`}
                            />
                          </button>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center justify-end gap-0.5">
                            <button
                              onClick={() => navigate(`/employees/${employee.slug || employee.id}`)}
                              className="p-1.5 text-text-muted hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                              title="View"
                            >
                              <Eye size={15} />
                            </button>
                            <button
                              onClick={() => navigate(`/employees/${employee.slug || employee.id}/edit`)}
                              className="p-1.5 text-text-muted hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                              title="Edit"
                            >
                              <Edit3 size={15} />
                            </button>
                            {isSuperAdmin && !!linkUserId && (
                              <button
                                onClick={() => handleSwitchAccount(employee)}
                                disabled={switchingId === employee.id}
                                className="p-1.5 text-text-muted hover:text-primary hover:bg-primary/10 rounded-md transition-colors disabled:opacity-50"
                                title={`Switch to ${name}'s account`}
                              >
                                {switchingId === employee.id ? (
                                  <Loader2 size={15} className="animate-spin" />
                                ) : (
                                  <LogIn size={15} />
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => openToggleConfirm(employee)}
                              className={`p-1.5 rounded-md transition-colors ${
                                employee.isActive
                                  ? 'text-success hover:bg-success/10'
                                  : 'text-danger hover:bg-danger/10'
                              }`}
                              title={employee.isActive ? 'Deactivate' : 'Activate'}
                            >
                              {employee.isActive ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                            </button>
                            <button
                              onClick={() => requestDeleteOne(employee)}
                              disabled={self}
                              className="p-1.5 text-text-muted hover:text-danger hover:bg-danger/10 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-text-muted"
                              title={self ? 'You cannot delete your own account' : 'Delete'}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="employees"
          />
        </div>
      )}

      <ConfirmDialog
        open={!!deleteRequest}
        title={
          deleteRequest && deleteRequest.ids.length > 1
            ? `Delete ${deleteRequest.ids.length} employees?`
            : `Delete "${deleteRequest?.label}"?`
        }
        description="This will move the selected record to the Trash. You can restore it later."
        confirmLabel={
          deleteRequest && deleteRequest.ids.length > 1
            ? `Delete ${deleteRequest.ids.length} employees`
            : 'Delete'
        }
        loading={deletePending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteRequest(null)}
      />
    </div>
  );
}
